import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Eye, EyeOff, UserPlus, Loader2, MapPin, CheckCircle, Shield, Users, Heart, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_caregiver-hub-25/artifacts/7i9n9nvr_Zorg%20Vleugel%20Logo%20Icoon%20Rood.png";

export default function RegisterPage() {
  const [searchParams] = useSearchParams();
  const inviteCode = searchParams.get('code') || '';
  
  const [code, setCode] = useState(inviteCode);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [inviteInfo, setInviteInfo] = useState(null);
  
  const { registerWithInvite, user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (user) {
      navigate('/dashboard');
    }
  }, [user, navigate]);

  useEffect(() => {
    if (code.length >= 6) {
      verifyCode();
    } else {
      setInviteInfo(null);
    }
  }, [code]);

  const verifyCode = async () => {
    setVerifying(true);
    try {
      const response = await axios.get(`${API_URL}/api/invites/verify/${code}`);
      setInviteInfo(response.data);
    } catch (error) {
      setInviteInfo(null);
      if (code.length >= 8) {
        toast.error('Ongeldige of verlopen uitnodigingscode');
      }
    } finally {
      setVerifying(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    
    if (password !== confirmPassword) {
      toast.error('Wachtwoorden komen niet overeen');
      return;
    }

    if (password.length < 8) {
      toast.error('Wachtwoord moet minimaal 8 tekens bevatten');
      return;
    }
    
    setLoading(true);
    
    try {
      await registerWithInvite({
        invite_code: code,
        name,
        email,
        password
      });
      toast.success('Registratie succesvol! Je kunt nu inloggen.');
      navigate('/login');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registratie mislukt');
    } finally {
      setLoading(false);
    }
  };

  const benefits = [
    { icon: Heart, text: "Direct toegang tot AI-assistent", color: "text-rose-400" },
    { icon: Users, text: "Cliëntinformatie altijd bij de hand", color: "text-sky-400" },
    { icon: Shield, text: "Alle protocollen op één plek", color: "text-emerald-400" },
    { icon: Sparkles, text: "Slimme herinneringen", color: "text-amber-400" },
  ];

  return (
    <div className="min-h-screen flex bg-slate-50">
      {/* Left side - Branding & Benefits */}
      <div className="hidden lg:flex lg:w-[55%] relative overflow-hidden bg-gradient-to-br from-[#1e3a5f] via-[#2d5a87] to-[#1e3a5f]">
        {/* Decorative elements */}
        <div className="absolute top-0 right-0 w-96 h-96 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/2" />
        <div className="absolute bottom-0 left-0 w-64 h-64 bg-white/5 rounded-full translate-y-1/2 -translate-x-1/2" />
        <div className="absolute top-1/3 right-1/4 w-32 h-32 bg-[#d4a853]/20 rounded-full blur-2xl" />
        <div className="absolute bottom-1/3 left-1/4 w-24 h-24 bg-emerald-500/20 rounded-full blur-xl" />
        
        {/* Content */}
        <div className="relative z-10 flex flex-col justify-between p-12 w-full">
          {/* Logo & Title */}
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="flex items-center gap-4">
              <img 
                src={LOGO_URL} 
                alt="Zorg-Vleugel" 
                className="w-14 h-14 drop-shadow-lg"
              />
              <div>
                <h2 className="text-2xl font-bold text-white">Zorg-Vleugel</h2>
                <p className="text-white/60 text-sm">Begeleid Wonen Dashboard</p>
              </div>
            </div>
          </motion.div>

          {/* Main Content */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 0.6 }}
            className="space-y-8"
          >
            <div>
              <h1 className="text-4xl lg:text-5xl font-bold text-white leading-tight">
                Word onderdeel<br />
                <span className="text-[#d4a853]">van het team</span>
              </h1>
              <p className="text-white/70 text-lg mt-4 max-w-lg">
                Met je uitnodigingscode krijg je direct toegang tot alle tools 
                die je nodig hebt voor je werk als begeleider.
              </p>
            </div>

            {/* Benefits Grid */}
            <div className="grid grid-cols-2 gap-4">
              {benefits.map((benefit, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.5 + index * 0.1, duration: 0.4 }}
                  className="flex items-center gap-3 bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/10"
                >
                  <div className={`p-2 rounded-lg bg-white/10 ${benefit.color}`}>
                    <benefit.icon className="w-5 h-5" />
                  </div>
                  <span className="text-white/90 text-sm font-medium">{benefit.text}</span>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Footer */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.8 }}
            className="text-white/40 text-sm"
          >
            © 2026 Zorg-Vleugel • Begeleid Wonen
          </motion.div>
        </div>
      </div>

      {/* Right side - Register form */}
      <div className="w-full lg:w-[45%] flex items-center justify-center p-6 lg:p-12 overflow-y-auto">
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5 }}
          className="w-full max-w-md"
        >
          <div className="space-y-6">
            {/* Mobile Logo */}
            <div className="lg:hidden flex items-center justify-center gap-3 mb-6">
              <img src={LOGO_URL} alt="Zorg-Vleugel" className="w-12 h-12" />
              <div>
                <h2 className="text-xl font-bold text-[#1e3a5f]">Zorg-Vleugel</h2>
                <p className="text-slate-500 text-xs">Begeleid Wonen</p>
              </div>
            </div>

            {/* Header */}
            <div className="text-center lg:text-left">
              <h1 className="text-3xl font-bold text-slate-900">Registreren</h1>
              <p className="text-slate-500 mt-2">
                Maak een account aan met je uitnodigingscode
              </p>
            </div>

            {/* Register Form */}
            <form onSubmit={handleRegister} className="space-y-4">
              {/* Invite Code */}
              <div className="space-y-2">
                <Label htmlFor="code" className="text-slate-700 font-medium">
                  Uitnodigingscode *
                </Label>
                <div className="relative">
                  <Input
                    id="code"
                    type="text"
                    placeholder="ABCD1234"
                    value={code}
                    onChange={(e) => setCode(e.target.value.toUpperCase())}
                    required
                    data-testid="register-invite-code-input"
                    className="h-12 bg-white border-slate-200 focus:border-[#1e3a5f] focus:ring-[#1e3a5f]/20 rounded-xl uppercase tracking-wider pr-12"
                  />
                  {verifying && (
                    <Loader2 className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 animate-spin text-slate-400" />
                  )}
                  {inviteInfo && (
                    <CheckCircle className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 text-emerald-500" />
                  )}
                </div>
                {inviteInfo && (
                  <motion.div 
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    className="flex items-center gap-2 text-sm text-emerald-700 bg-emerald-50 p-3 rounded-xl border border-emerald-200"
                  >
                    <MapPin className="w-4 h-4" />
                    <span className="font-medium">{inviteInfo.location?.name}</span>
                    <span className="text-emerald-600">• {inviteInfo.role}</span>
                  </motion.div>
                )}
              </div>

              {/* Name */}
              <div className="space-y-2">
                <Label htmlFor="name" className="text-slate-700 font-medium">
                  Volledige naam *
                </Label>
                <Input
                  id="name"
                  type="text"
                  placeholder="Jan de Vries"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                  data-testid="register-name-input"
                  className="h-12 bg-white border-slate-200 focus:border-[#1e3a5f] focus:ring-[#1e3a5f]/20 rounded-xl"
                />
              </div>

              {/* Email */}
              <div className="space-y-2">
                <Label htmlFor="email" className="text-slate-700 font-medium">
                  E-mailadres *
                </Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="naam@zorg-vleugel.nl"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  data-testid="register-email-input"
                  className="h-12 bg-white border-slate-200 focus:border-[#1e3a5f] focus:ring-[#1e3a5f]/20 rounded-xl"
                />
              </div>
              
              {/* Password */}
              <div className="space-y-2">
                <Label htmlFor="password" className="text-slate-700 font-medium">
                  Wachtwoord *
                </Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Minimaal 8 tekens"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    minLength={8}
                    data-testid="register-password-input"
                    className="h-12 bg-white border-slate-200 focus:border-[#1e3a5f] focus:ring-[#1e3a5f]/20 rounded-xl pr-12"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              {/* Confirm Password */}
              <div className="space-y-2">
                <Label htmlFor="confirmPassword" className="text-slate-700 font-medium">
                  Bevestig wachtwoord *
                </Label>
                <Input
                  id="confirmPassword"
                  type="password"
                  placeholder="Herhaal wachtwoord"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  data-testid="register-confirm-password-input"
                  className="h-12 bg-white border-slate-200 focus:border-[#1e3a5f] focus:ring-[#1e3a5f]/20 rounded-xl"
                />
              </div>

              {/* Submit Button */}
              <Button 
                type="submit" 
                className="w-full h-12 rounded-xl text-base font-semibold bg-gradient-to-r from-[#1e3a5f] to-[#2d5a87] hover:from-[#2d5a87] hover:to-[#3d6a97] shadow-lg shadow-[#1e3a5f]/25 transition-all duration-300 mt-2"
                disabled={loading || !inviteInfo}
                data-testid="register-submit-btn"
              >
                {loading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <>
                    <UserPlus className="w-5 h-5 mr-2" />
                    Registreren
                  </>
                )}
              </Button>
            </form>

            {/* Divider */}
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-slate-200"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-4 bg-slate-50 text-slate-400">of</span>
              </div>
            </div>

            {/* Login Link */}
            <div className="text-center">
              <p className="text-slate-600">
                Heb je al een account?{' '}
                <Link 
                  to="/login" 
                  className="text-[#1e3a5f] hover:text-[#2d5a87] font-semibold transition-colors"
                >
                  Log hier in
                </Link>
              </p>
            </div>

            {/* Help Text */}
            <div className="bg-[#1e3a5f]/5 rounded-xl p-4 border border-[#1e3a5f]/10">
              <p className="text-sm text-slate-600 text-center">
                Geen uitnodigingscode? Vraag deze aan bij je teamleider of beheerder.
              </p>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
